import { takeEvery, put, select, all } from 'redux-saga/effects';
import { toastr } from 'react-redux-toastr';

import apiFetcher from '../tools/api-fetcher';
import { parseHttpStatusText } from '../tools';
import handleError from '../tools/error-helper';

import * as types from '../actions/applications/types';
import * as actions from '../actions/applications';
import config from '../config';

import applicationSubstatuses from '../constants/application-substatuses';
import applicationSubstatusNames from '../constants/application-substatus-names';
import { getVacancyApplications, getVacancySummary } from '../actions/vacancies';
import { setVacancySuggestedToClose } from '../actions/vacancy-close';

const applicationActiveSubstatusUrlEndings = {
  [applicationSubstatuses.offerMade]: 'offer-made',
  [applicationSubstatuses.possibleMatch]: 'possible-match',
  [applicationSubstatuses.shortlisted]: 'shortlisted'
};

const applicationDecisionMadeSubstatusUrlEndings = {
  [applicationSubstatuses.hired]: 'hired',
  [applicationSubstatuses.offerDeclined]: 'offer-declined',
  [applicationSubstatuses.notAFit]: 'not-a-fit',
  [applicationSubstatuses.notSelected]: 'not-selected'
};

const updateApplicationIntermediateSubstatusServerCall = (schoolId, vacancyId, applicationId, substatus) => {
  const url = getApplicationIntermediateSubstatusUrl(schoolId, vacancyId, substatus);
  
  return apiFetcher.auth().post(url, { applicationId });
};

const cancelApplicationIntermediateSubstatusServerCall = (schoolId, vacancyId, applicationId, substatus) => {
  const url = getApplicationIntermediateSubstatusUrl(schoolId, vacancyId, substatus);

  return apiFetcher.auth().delete(url + '/' + applicationId );
};

const getApplicationIntermediateSubstatusUrl = (schoolId, vacancyId, substatus) => {
  const substatusUrlEnding = applicationActiveSubstatusUrlEndings[substatus];

  return `/schools/${schoolId}/vacancies/${vacancyId}/applications/active/${substatusUrlEnding}`;
}

const recordApplicationFinalDecisionServerCall = (schoolId, vacancyId, applicationId, substatus, emailTemplate) => {
  const substatusUrlEnding = applicationDecisionMadeSubstatusUrlEndings[substatus];

  return apiFetcher.auth().post(`/schools/${schoolId}/vacancies/${vacancyId}/applications/decision-made/${substatusUrlEnding}`, { applicationId, emailTemplate });
}

function* getApplicationsSummary({ schoolId, vacancyId, applicationId }) {
  yield put(actions.setSummaryLoading(true));

  try {
    const { data } = yield apiFetcher.auth().get(`/schools/${schoolId}/vacancies/${vacancyId}/applications/${applicationId}/summary`);

    yield put(actions.setApplicationSummary(data));
  } catch (exception) {
    if (exception.response) {
      const { status, statusText } = exception.response;

      if (status === 403 || status === 404) {
        const parsedStatusText = parseHttpStatusText(statusText);

        const { text, subcode } = parsedStatusText;

        yield put(actions.setSummaryError({ text, subcode }));
      } else {
        handleError(exception, `Failed to get application summary.`);
      }
    } else {
      handleError(exception);
    }
  }

  yield put(actions.setSummaryLoading(false));
}

function* getApplicationRating({ schoolId, vacancyId, applicationId }) {
  yield put(actions.setRatingLoading(true));

  try {
    const { data } = yield apiFetcher.auth().get(`/schools/${schoolId}/vacancies/${vacancyId}/applications/${applicationId}/rating`);

    yield put(actions.setApplicationRating(data));
  } catch (exception) {
    handleError(exception);
  }

  yield put(actions.setRatingLoading(false));
}

function* getApplicationSchoolUserRatings({ schoolId, vacancyId, applicationId }) {
  yield put(actions.setSchoolUserRatingsLoading(true));

  try {
    const { data } = yield apiFetcher.auth().get(`/schools/${schoolId}/vacancies/${vacancyId}/applications/${applicationId}/rating/school-user-ratings`);

    yield put(actions.setApplicationSchoolUserRatings(data));
  } catch (exception) {
    handleError(exception);
  }

  yield put(actions.setSchoolUserRatingsLoading(false));
}

function* updateApplicationCurrentSchoolUserRating({ schoolId, vacancyId, applicationId, rate }) {
  yield put(actions.setUpdateRatingLoading(true));

  try {
    yield apiFetcher.auth().put(`/schools/${schoolId}/vacancies/${vacancyId}/applications/${applicationId}/rating/school-user-ratings/current`, rate);

    yield put(actions.getApplicationRating(schoolId, vacancyId, applicationId));
    yield put(actions.getApplicationSchoolUserRatings(schoolId, vacancyId, applicationId));
    yield put(actions.getApplicationSummary(schoolId, vacancyId, applicationId));
  } catch (exception) {
    handleError(exception);
  }

  yield put(actions.setUpdateRatingLoading(false));
}

function* deleteApplicationCurrentSchoolUserRating({ schoolId, vacancyId, applicationId }) {
  yield put(actions.setDeleteRatingLoading(true));

  try {
    yield apiFetcher.auth().delete(`/schools/${schoolId}/vacancies/${vacancyId}/applications/${applicationId}/rating/school-user-ratings/current`);

    yield put(actions.getApplicationRating(schoolId, vacancyId, applicationId));
    yield put(actions.getApplicationSchoolUserRatings(schoolId, vacancyId, applicationId));
    yield put(actions.getApplicationSummary(schoolId, vacancyId, applicationId));
  } catch (exception) {
    handleError(exception);
  }

  yield put(actions.setDeleteRatingLoading(false));
}

function* getApplicationCommentList({ schoolId, vacancyId, applicationId }) {
  yield put(actions.setApplicationCommentListLoading(true));
  try {
    const { data } = yield apiFetcher.auth().get(`/schools/${schoolId}/vacancies/${vacancyId}/applications/${applicationId}/comments`);
    yield put(actions.setApplicationCommentList(data));
  } catch (exception) {
    handleError(exception);
  }
  yield put(actions.setApplicationCommentListLoading(false));
}

function* createApplicationComment({ schoolId, vacancyId, applicationId, parentCommentId, text, important }) {
  yield put(actions.setApplicationCommentCreating(true));
  let body = {
    parentCommentId: parentCommentId,
    commentDraft: {
      text: text,
      important: important
    }
  }
   try {
    yield apiFetcher.auth().post(`/schools/${schoolId}/vacancies/${vacancyId}/applications/${applicationId}/comments`, body);

    yield put(actions.getApplicationCommentList(schoolId, vacancyId, applicationId));
  } catch (exception) {
    handleError(exception);
  }
  yield put(actions.setApplicationCommentCreating(false));
}

function* updateApplicationComment({ schoolId, vacancyId, applicationId, commentId, text, important }) {
  yield put(actions.setApplicationCommentUpdating(true));
  let body = {
    commentDraft: {
      text: text,
      important: important
    }
  }
   try {
    yield apiFetcher.auth().put(`/schools/${schoolId}/vacancies/${vacancyId}/applications/${applicationId}/comments/${commentId}`, body);

    yield put(actions.getApplicationCommentList(schoolId, vacancyId, applicationId));
  } catch (exception) {
    handleError(exception);
  }
  yield put(actions.setApplicationCommentUpdating(false));
}

function* deleteApplicationComment({ schoolId, vacancyId, applicationId, commentId }) {
  yield put(actions.setApplicationCommentDeleting(true));
   try {
    yield apiFetcher.auth().delete(`/schools/${schoolId}/vacancies/${vacancyId}/applications/${applicationId}/comments/${commentId}`);
    
    yield put(actions.getApplicationCommentList(schoolId, vacancyId, applicationId));
  } catch (exception) {
    handleError(exception);
  }
  yield put(actions.setApplicationCommentDeleting(false));
}

function* getApplicationCoverLetter({ schoolId, vacancyId, applicationId }) {
  yield put(actions.setApplicationCoverLetterLoading(true));

  try {
    const { data } = yield apiFetcher.auth().get(`/schools/${schoolId}/vacancies/${vacancyId}/applications/${applicationId}/details/cover-letter`);

    yield put(actions.setApplicationCoverLetter(data));
  } catch (exception) {
    yield handleError(exception);
  }

  yield put(actions.setApplicationCoverLetterLoading(false));
}

function* getApplicationSupplementaryQuestions({ schoolId, vacancyId, applicationId }) {
  yield put(actions.setApplicationSupplementaryQuestionsLoading(true));

  try {
    const { data } = yield apiFetcher.auth().get(`/schools/${schoolId}/vacancies/${vacancyId}/applications/${applicationId}/details/supplementary-questions`);

    yield put(actions.setApplicationSupplementaryQuestions(data));
  } catch (exception) {
    yield handleError(exception);
  }

  yield put(actions.setApplicationSupplementaryQuestionsLoading(false));
}

function* reviewVacancyApplicationForSchoolUser({schoolId, vacancyId, applicationId}) {
  try {
    yield apiFetcher
          .auth()
          .post(`/schools/${schoolId}/vacancies/${vacancyId}/applications/${applicationId}/reviews`);
  } catch (exception) {
    if (config.debug) {
      console.exception(exception);
    }
  }
}

function* reloadVacancyApplicationContext(schoolId, vacancyId, applicationId) {
  const applicationList = yield select(state => state.vacancyApplications);
  const application = yield select(state => state.applicationDetails);
  const vacancy = yield select(state => state.vacancyDetails);

  if (applicationId && application.get('applicationSummary')) {
    yield put(actions.getApplicationSummary(schoolId, vacancyId, applicationId));
  }

  if (applicationList.get('results')) {
    yield put(getVacancyApplications(schoolId, vacancyId, applicationList.get('queryParams')));
  }

  if (vacancy.get('vacancySummary').get('vacancyId') === vacancyId) {
    yield put(getVacancySummary(schoolId, vacancyId));
  }
}

function* updateApplicationIntermediateSubstatus({ schoolId, vacancyId, applicationId, substatus }) {
  yield put(actions.setApplicationIntermediateSubstatusUpdating(true));

  try {
    yield updateApplicationIntermediateSubstatusServerCall(schoolId, vacancyId, applicationId, substatus);

    yield reloadVacancyApplicationContext(schoolId, vacancyId, applicationId);

    yield toastr.success(`Application was marked as ${applicationSubstatusNames[substatus]}`);
  } catch (exception) {
    yield handleError(exception);
  }

  yield put(actions.setApplicationIntermediateSubstatusUpdating(false));
}

function* cancelApplicationIntermediateSubstatus({ schoolId, vacancyId, applicationId, substatus }) {
  yield put(actions.setApplicationIntermediateSubstatusUpdating(true));

  try {
    yield cancelApplicationIntermediateSubstatusServerCall(schoolId, vacancyId, applicationId, substatus);

    yield reloadVacancyApplicationContext(schoolId, vacancyId, applicationId);

    yield toastr.success(`Mark ${applicationSubstatusNames[substatus]} was removed from the application.`);
  } catch (exception) {
    yield handleError(exception);
  }

  yield put(actions.setApplicationIntermediateSubstatusUpdating(false));
}

function* updateApplicationBatchIntermediateSubstatus({
  schoolId,
  vacancyId,
  applicationIds,
  substatus
}) {
  yield put(actions.setApplicationBatchIntermediateSubstatusUpdating(true));

  let successful = 0;
  let failed = 0

  yield put(actions.setApplicationBatchIntermediateSubstatusOperationData({ successful, failed }));

  for (let i = 0; i < applicationIds.length; i++) {
    try {
      yield updateApplicationIntermediateSubstatusServerCall(schoolId, vacancyId, applicationIds[i], substatus);

      successful += 1;
    } catch (exception) {
      failed +=1;
    } finally {
      yield put(actions.setApplicationBatchIntermediateSubstatusOperationData({ successful, failed }));
    }
  }

  if (successful) {
    yield reloadVacancyApplicationContext(schoolId, vacancyId);
  }

  yield put(actions.setApplicationBatchIntermediateSubstatusUpdating(false));
}

function* recordApplicationDecisionMade({ schoolId, vacancyId, applicationId, template, substatus }) {
  yield put(actions.setApplicationFinalDecisionUpdating(true));

  try {
    yield recordApplicationFinalDecisionServerCall(schoolId, vacancyId, applicationId, substatus, template);

    yield toastr.success(`Your decision for this application has been successfully recorded.${template ? ` An email has been sent to this applicant informing him or her of this status change.` : ''}`);
    
    if(substatus === applicationSubstatuses.hired) {
      const vacancy = yield select(state => state.vacancyDetails);
      if(vacancy.get('vacancySummary').get('openings') === vacancy.get('vacancySummary').get('filledOpeningsCount') + 1) {
        yield put(setVacancySuggestedToClose(schoolId, vacancyId));
      }
    }

    yield reloadVacancyApplicationContext(schoolId, vacancyId, applicationId);
  } catch (exception) {
    yield handleError(exception);
  }
  yield put(actions.setApplicationFinalDecisionUpdating(false));
}

function* recordApplicationBatchDecisionMade({ schoolId, vacancyId, applicationIds, substatus }) {
  yield put(actions.setApplicationBatchFinalDecisionUpdating(true));

  let successful = 0;
  let failed = 0

  yield put(actions.setApplicationBatchFinalDecisionOperationData({ successful, failed }));

  for (let i = 0; i < applicationIds.length; i++) {
    try {
      yield recordApplicationFinalDecisionServerCall(schoolId, vacancyId, applicationIds[i], substatus);

      successful += 1;
    } catch (exception) {
      failed +=1;
    } finally {
      yield put(actions.setApplicationBatchFinalDecisionOperationData({ successful, failed }));
    }
  }

  if (successful) {
    yield reloadVacancyApplicationContext(schoolId, vacancyId);
  }

  yield put(actions.setApplicationBatchFinalDecisionUpdating(false));
}

function* cancelApplicationFinalDecision({ schoolId, vacancyId, applicationId, emailTemplate }) {
  yield put(actions.setApplicationFinalDecisionCanceling(true));

  try {
    yield apiFetcher.auth().post(`/schools/${schoolId}/vacancies/${vacancyId}/applications/decision-made/${applicationId}/cancellation`, { emailTemplate });

    yield reloadVacancyApplicationContext(schoolId, vacancyId, applicationId);

    yield toastr.success('The final decision on the application was canceled.');
  } catch (exception) {
    yield handleError(exception);
  }

  yield put(actions.setApplicationFinalDecisionCanceling(false));
}

function* getApplicationDocuments({ schoolId, vacancyId, applicationId }) {
  yield put(actions.setApplicationDocumentsLoading(true));

  try {
    const { data } = yield apiFetcher.auth().get(`/schools/${schoolId}/vacancies/${vacancyId}/applications/${applicationId}/details/documents/summaries`);

    yield put(actions.setApplicationDocuments(data));
  } catch (exception) {
    yield handleError(exception);
  }

  yield put(actions.setApplicationDocumentsLoading(false));
}

export default function* applicationsSaga() {
  yield all([
    takeEvery(types.APPLICATION_GET_SUMMARY, getApplicationsSummary),
    takeEvery(types.APPLICATION_GET_RATING, getApplicationRating),
    takeEvery(types.APPLICATION_GET_SCHOOL_USER_RATINGS, getApplicationSchoolUserRatings),
    takeEvery(types.APPLICATION_UPDATE_CURRENT_SCHOOL_USER_RATING, updateApplicationCurrentSchoolUserRating),
    takeEvery(types.APPLICATION_DELETE_CURRENT_SCHOOL_USER_RATING, deleteApplicationCurrentSchoolUserRating),
    takeEvery(types.APPLICATION_GET_COMMENT_LIST, getApplicationCommentList),
    takeEvery(types.APPLICATION_CREATE_COMMENT, createApplicationComment),
    takeEvery(types.APPLICATION_UPDATE_COMMENT, updateApplicationComment),
    takeEvery(types.APPLICATION_DELETE_COMMENT, deleteApplicationComment),
    takeEvery(types.APPLICATION_GET_COVER_LETTER, getApplicationCoverLetter),
    takeEvery(types.APPLICATION_GET_SUPPLEMENTARY_QUESTIONS, getApplicationSupplementaryQuestions),
    takeEvery(types.APPLICATION_REVIEW_VACANCY_APPLICATION_FOR_SCHOOL_USER, reviewVacancyApplicationForSchoolUser),
    takeEvery(types.APPLICATION_UPDATE_INTERMEDIATE_SUBSTATUS, updateApplicationIntermediateSubstatus),
    takeEvery(types.APPLICATION_CANCEL_INTERMEDIATE_SUBSTATUS, cancelApplicationIntermediateSubstatus),
    takeEvery(types.APPLICATION_UPDATE_BATCH_INTERMEDIATE_SUBSTATUS, updateApplicationBatchIntermediateSubstatus),
    takeEvery(types.APPLICATION_UPDATE_FINAL_DECISION, recordApplicationDecisionMade),
    takeEvery(types.APPLICATION_UPDATE_BATCH_FINAL_DECISION, recordApplicationBatchDecisionMade),
    takeEvery(types.APPLICATION_CANCEL_FINAL_DECISION, cancelApplicationFinalDecision),
    takeEvery(types.APPLICATION_GET_DOCUMENTS, getApplicationDocuments)
  ]);
}


// WEBPACK FOOTER //
// ./src/sagas/applications.js